import {
  FieldError, FieldErrors, FieldValues,
} from 'react-hook-form';
import CloseIcon from 'assets/icons/close.svg';
import { FormGroupModifier } from 'models/form';
import React, { ChangeEventHandler, FocusEventHandler } from 'react';
import useModifiers from 'hooks/useModifiers';
import { TextfieldModifier } from './textfield';

export interface DatepickerTextfieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  errors?: FieldErrors<FieldValues>;
  value: string | number | readonly string[] | undefined;
  onClick: React.MouseEventHandler<HTMLInputElement> | undefined;
  onChange: ChangeEventHandler<HTMLInputElement> | undefined;
  onBlur: FocusEventHandler<HTMLInputElement> | undefined;
  isOpen: boolean;
  innerRef: React.ForwardedRef<HTMLInputElement>;
  onClearCB: () => void;
  clearable?: boolean;
  modifiers?: TextfieldModifier[];
}

// eslint-disable-next-line max-len
type PropT = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & Record<string, unknown>;

type RefType = React.ForwardedRef<HTMLInputElement>;

export default function DatepickerTextfield(props: DatepickerTextfieldProps) {
  const mods = useModifiers(props.modifiers);
  const {
    name, disabled = false, label = '', placeholder, errors, value, onClick, onChange, onBlur, innerRef, isOpen, onClearCB, clearable,
  } = props;

  const error = errors && errors[name] as FieldError;
  const hasError = Boolean(error);

  const hasErrorMod = hasError ? FormGroupModifier.HAS_ERROR : '';
  const disabledMod = disabled ? FormGroupModifier.DISABLED : '';
  const activeMod = isOpen ? FormGroupModifier.ACTIVE : '';

  return (
    <div className={`ct_form-group ${hasErrorMod} ${disabledMod} ${activeMod}`}>
      <div className="ct_form-desc">
        {!!label && (<div className="ct_form-label">{label}</div>)}
      </div>
      <div className="ct_form-control">
        <input
          ref={innerRef}
          className={`ct_textfield ${mods}`}
          type="text"
          value={value}
          placeholder={placeholder}
          onClick={onClick}
          onChange={onChange}
          onBlur={onBlur}
        />
        {!!clearable && value && (
        <button type="button" onClick={() => onClearCB()} className="ct_textfield-btn">
          <CloseIcon />
        </button>
        )}
      </div>
      {hasError && error && (
        <div className="ct_form-msg">
          Error:
          {error.type || ''}
        </div>
      )}
    </div>
  );
}

DatepickerTextfield.defaultProps = { ...{} };

// eslint-disable-next-line react/display-name
export const DatePickerInputWrapper = React.forwardRef((props: PropT, ref: RefType) => {
  const {
    name,
    disabled,
    placeholder,
    value,
    onClick,
    onChange,
    onBlur,
    errors,
    label,
    isOpen,
    onClearCB,
    clearable,
    modifiers,
  } = props;

  return (
    <DatepickerTextfield
      innerRef={ref}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      isOpen={isOpen as boolean}
      value={value}
      name={name as string}
      placeholder={placeholder}
      disabled={disabled}
      label={label as string}
      errors={errors as FieldErrors<FieldValues>}
      onClearCB={() => (onClearCB as () => void)()}
      clearable={clearable as boolean}
      modifiers={modifiers as TextfieldModifier[]}
    />
  );
});
